import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import IconButton, { IconButtonStyle } from "@pacific/App/Components/Common/IconButton";
import { CustomAppState } from "@pacific/App/Store/Reducers";
import LazyImage, { LazyImagePresentationProps, LazyImageProps } from "@pacific/MobiusOverrides/LazyImage/LazyImage";
import CaretRight from "@pacific/XDL/Icons/CaretRight";
import Next from "@pacific/XDL/Icons/Next";
import Previous from "@pacific/XDL/Icons/Previous";
import { headingsFontConfig } from "@pacific/XDL/PreStyle/typography";
import { xBlueprintName } from "@pacific/XDL/types";
import { layoutContainer, xScreen } from "@pacific/XDL/Utilities";
import color from "@pacific/XDL/Utilities/color";
import * as cssLinter from "css";
import { EmblaCarouselType } from "embla-carousel/embla-carousel-vanilla";
import { useEmblaCarousel } from "embla-carousel/react";
import parse from "html-react-parser";
import React, { useCallback, useEffect, useState } from "react";
import { css } from "styled-components";

interface SlideModel {
    fields: {
        slideTitle: string;
        background: "image" | "color";
        image: string;
        imageLink: LinkFieldValue;
        // imageOverlay: string;
        // partialOverlay: boolean;
        // partialOverlayPositioning: "top" | "middle" | "bottom";
        // responsiveImageBehavior: "cover" | "center" | "prioritizeHeight" | "prioritizeWidth";
        backgroundColor: string;
        heading: string;
        subheading: string;
        buttonLabel: string;
        buttonLink: LinkFieldValue;
        buttonVariant: "primary" | "secondary" | "tertiary" | "special";
        // focalPoint:
        //     | "topLeft"
        //     | "topCenter"
        //     | "topRight"
        //     | "centerLeft"
        //     | "center"
        //     | "centerRight"
        //     | "bottomLeft"
        //     | "bottomCenter"
        //     | "bottomRight";
        contentPadding: number;
        // centerTextVertically: boolean;
        customCSS: string;
        // XNG-Change
        imageLinkElementId: string;
        buttonElementId: string;
        mediumImage: string;
        smallImage: string;
        imageAltText: string;
        secondButtonLabel: string;
        secondButtonLink: LinkFieldValue;
        secondButtonElementId: string;
        secondButtonVariant: "primary" | "secondary" | "tertiary" | "special";
        openInNewTab: boolean;
        openInNewTab2: boolean;
        removeImageIndentation: boolean;
        linkOrParagraph: "paragraph" | "link";
    };
}

const enum fields {
    height = "height",
    textAlignment = "textAlignment",
    showArrows = "showArrows",
    slideIndicator = "slideIndicator",
    autoplay = "autoplay",
    slides = "slides",
    responsiveFontSizes = "responsiveFontSizes",
    customFontSizes = "customFontSizes",
    h1FontSize = "h1FontSize",
    h2FontSize = "h2FontSize",
    h3FontSize = "h3FontSize",
    h4FontSize = "h4FontSize",
    h5FontSize = "h5FontSize",
    h6FontSize = "h6FontSize",
    normalFontSize = "normalFontSize",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.height]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.textAlignment]: "left" | "center" | "right";
        [fields.showArrows]: boolean;
        [fields.slideIndicator]: boolean;
        [fields.autoplay]: number;
        [fields.responsiveFontSizes]: boolean;
        [fields.customFontSizes]: boolean;
        [fields.h1FontSize]: number;
        [fields.h2FontSize]: number;
        [fields.h3FontSize]: number;
        [fields.h4FontSize]: number;
        [fields.h5FontSize]: number;
        [fields.normalFontSize]: number;
        [fields.slides]: SlideModel[];
        [fields.customCSS]: string;
    };
    extendedStyles?: SlideshowStyles;
}

export interface SlideshowStyles {
    slideshowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideOverlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    headingText?: TypographyPresentationProps;
    subheadingText?: TypographyPresentationProps;
    slideButton?: ButtonPresentationProps;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
    // XNG-Change
    slidesContainer?: InjectableCss;
    buttonsWrapper?: InjectableCss;
    specialButton?: IconButtonStyle;
    backgroundWrapper?: InjectableCss;
    image?: LazyImageProps;
    clickableBackground?: ButtonPresentationProps;
    identationBox?: InjectableCss;
    imageWithIdentation?: LazyImageProps;
    imageWithoutIdentation?: LazyImageProps;
    imageWrapper?: InjectableCss;
}

export const slideshowStyles: SlideshowStyles = {
    slidesContainer: {
        css: css`
            background: ${color("common.ui2")};
            ${xScreen("lte", "md")} {
                background: none;
            }
        `,
    },
    slideshowWrapper: {
        css: css`
            position: relative;
            ${xScreen("gt", "md")} {
                ${layoutContainer}
            }
        `,
    },
    prevArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            margin-top: -20px;
        `,
    },
    prevArrowButton: {
        css: css`
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    iconProps: {
        size: 35,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;
            background-color: ${color("common.ui2")};
            ${xScreen("lte", "md")} {
                background: none;
            }
        `,
    },
    slideContentWrapper: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            align-items: flex-start;
            position: relative;

            ${({ slidesHeight }: any) => {
                if (slidesHeight) {
                    return css`
                        max-height: ${slidesHeight};
                    `;
                }
            }}

            ${xScreen("lte", "md")} {
                background: none;
                max-height: none;
            }
        `,
    },
    slideOverlayWrapper: {
        css: css`
            width: 100%;
            ${xScreen("lte", "md")} {
                display: flex;
                flex-direction: column;
                height: 100%;
            }
        `,
    },
    slideButton: {
        css: css`
            width: fit-content;
            &: first-child {
                margin-right: 24px;
            }
        `,
    },
    dotsContainerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            margin: 24px 20px 0;
            ${xScreen("lte", "md")} {
                margin-top: 0;
            }
        `,
    },
    dotButton: {
        css: css`
            height: 10px;
            width: 10px;
            padding: 0;
            margin-right: 8px;
            border-radius: 50%;
            background: ${color("common.border")};
            min-height: initial;
            border-color: ${color("common.border")};
            &:hover,
            &:active,
            &:focus {
                &&&& {
                    background: ${color("common.ui8")};
                    border-color: ${color("common.ui8")};
                    outline: none;
                }
            }
        `,
    },
    slideCenteringWrapperStyles: {
        css: css`
            background-color: ${color("common.ui1")};
            padding: 24px;
            color: ${color("text.main")};
            max-width: 50%;
            max-height: 80%;
            ${xScreen("lte", "md")} {
                width: 100%;
                box-shadow: 0 6px 0 0 ${color("common.ui7", 0.15)};
                padding: 24px 31px;
                margin-bottom: 24px;
                max-height: initial;
                max-width: initial;
            }
            ${xScreen("lte", "sm")} {
                padding: 24px 16px;
            }
        `,
    },
    headingText: {
        variant: "h2",
        css: css`
            margin-bottom: 16px;
            ${TypographyStyle} {
                font-family: ${headingsFontConfig[BLUEPRINT_NAME as xBlueprintName].fontFamily};
                font-size: 28px;
                font-weight: 800;
                letter-spacing: 0;
                line-height: 34px;
                margin-bottom: 0;
            }
        `,
    },
    subheadingText: {
        variant: "p",
        css: css`
            margin-bottom: 16px;
            ${TypographyStyle} {
                font-size: 15px;
                font-weight: 500;
                letter-spacing: 0;
                line-height: 24px;
                margin-bottom: 0;
            }
        `,
    },
    buttonsWrapper: {
        css: css`
            display: flex;
            ${({ textAlignment }: any) => {
                if (textAlignment === "right") {
                    return css`
                        justify-content: flex-end;
                    `;
                    // eslint-disable-next-line no-else-return
                } else if (textAlignment === "center") {
                    return css`
                        justify-content: center;
                    `;
                }
            }}
        `,
    },
    specialButton: {
        button: {
            css: css`
                min-height: 40px;
                .icon-container {
                    height: 100%;
                    width: 40px;
                }
                &:first-child {
                    margin-right: 24px;
                }
            `,
        },
        iconWrapper: {
            css: css`
                &&& ${IconWrapper}, &&& svg {
                    width: 10px;
                    height: 21px;
                }
            `,
        },
        label: {
            variant: "h6",
            css: css`
                padding: 0 20px;
                margin-bottom: 0;
                line-height: 16px;
            `,
        },
    },
    backgroundWrapper: {
        css: css`
            width: 100%;
            flex-grow: 1;
        `,
    },
    image: {
        css: css`
            max-height: 100%;
            width: 100%;
            img {
                height: auto;
                font-size: 15px;
                font-weight: 500;
                letter-spacing: 0;
                line-height: 24px;
                object-fit: cover;
            }
        `,
    },
    clickableBackground: {
        css: css`
            width: 100%;
            height: 100%;
            display: flex;
            align-items: flex-start;
            ${({ imageLinkUrl }: any) => {
                if (!imageLinkUrl) {
                    return css`
                        cursor: initial;
                    `;
                }
            }}
            ${xScreen("lte", "md")} {
                padding-bottom: 24px;
            }
        `,
    },
    identationBox: {
        css: css`
            min-width: 110px;
            width: 110px;
            height: 100%;
            background-color: ${color("common.ui2")};
        `,
    },
    imageWithoutIdentation: {
        css: css`
            width: 100%;
            height: auto;
            img {
                height: auto;
                width: 100%;
                max-height: 100%;
                max-width: 100%;
            }
            
        `,
    },
    imageWithIdentation: {
        width: "1298px",
        transformations: {
            width: 1298,
            tabletWidth: 1024,
            mobileWidth: 768,
        },
        css: css`
            height: auto;
            width: 100%;
            img {
                height: auto;
                width: 100%;
                max-height: 100%;
                max-width: 100%;
            }
        `,
    },
    imageWrapper: {
        css: css`
            display: flex;
            max-height: 100%;
            width: 100%;
        `,
    },
};

interface SlideShowArrowProps {
    dir: "prev" | "next";
    currentIndex: number;
    totalSlides: number;
    embla?: EmblaCarouselType;
}

const SlideShowArrow = ({ dir, currentIndex, embla, totalSlides }: SlideShowArrowProps) => {
    const icon = dir === "prev" ? Previous : Next;
    const step = dir === "prev" ? -1 : 1;
    const margin = `margin-${dir === "prev" ? "right" : "left"}: ${dir === "prev" ? "16" : "8"}px;`;

    const buttonProps = {
        onClick: () => {
            if (!embla) {
                return;
            }
            embla.scrollTo(currentIndex + step);
        },
        disabled: currentIndex + step < 0 || currentIndex + step > totalSlides - 1,
        "aria-label": `slidshow-go-${dir}`,
    };

    return (
        <Clickable
            {...buttonProps}
            css={css`
                ${margin}
                &:disabled  > ${IconWrapper} {
                    color: ${color("primary.main", 0.3)};
                }
                &:hover:not(:disabled) > ${IconWrapper} {
                    color: ${color("common.ui8")};
                }
            `}
            aria-label="slide arrow"
        >
            <ButtonIcon src={icon} size={16} color="primary.main" />
        </Clickable>
    );
};

const defaultCustomCss = `.slideshow-wrapper {
}

.slideshow-carousel {
}

.slideshow-slide-container {
}

.slide-wrapper {
}

.slide-heading {
}

.slide-subheading {
}

.slide-button {
}

.slide-indicator {
}

.slide-dot-button {
}

.slide-arrow-wrapper {
}

.slide-arrow {
}
`;

export const Slideshow = ({ fields, extendedStyles }: OwnProps) => {
    const { isTabletScreen, isMobileScreen } = useAppSelector(state => ({
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
    }));

    const { isInShell } = useShellContext();
    const history = useHistory();
    const buttonLinks = useGetLinks(fields.slides, o => o.fields.buttonLink);
    const secondButtonLinks = useGetLinks(fields.slides, o => o.fields.secondButtonLink || "");
    const imageLinks = useGetLinks(fields.slides, o => o.fields.imageLink || "");

    const styles = useMergeStyles("slideshow", slideshowStyles, extendedStyles);

    const [emblaRef, embla] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
        });
    }, [embla]);

    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }

        embla.scrollNext();
    }, [embla]);

    useRecursiveTimeout(scrollNext, fields.autoplay * 1000);

    useEffect(() => {
        if (!embla || !fields.slides || fields.slides.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            loop: true,
        });
    }, [embla, fields.slides]);

    const onClick = (url: string | undefined, openInNewTab: boolean) => {
        if (!url) {
            return;
        }

        // <XNG-Change>
        if (openInNewTab) {
            window.open(url);
            return;
        }
        // </XNG-Change>

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    if (!fields.slides || fields.slides.length === 0) {
        return null;
    }

    let slidesHeight: string;

    switch (fields.height) {
        case "1/4 viewport":
            slidesHeight = "250px";
            break;
        case "1/2 viewport":
            slidesHeight = "450px";
            break;
        case "3/4 viewport":
            slidesHeight = "650px";
            break;
        case "fullViewport":
            slidesHeight = "800px";
            break;
    }
    const textAlignStyles = `text-align: ${fields.textAlignment};`;

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    // XNG-Change: change structure to match design
    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper {...styles.slidesContainer}>
                <StyledWrapper className="slideshow-wrapper" {...styles.slideshowWrapper}>
                    <StyledWrapper className="slideshow-carousel" {...styles.carouselContainer} ref={emblaRef}>
                        <StyledWrapper className="slideshow-slide-container" {...styles.slideContainerWrapper}>
                            {fields.slides.map((slide, index) => {
                                const buttonLink = buttonLinks[index];
                                const secondButtonLink = secondButtonLinks[index];
                                const imageLink = imageLinks[index];

                                // let responsiveImageBehaviorStyles;
                                // if (slide.fields.responsiveImageBehavior === "cover") {
                                //     responsiveImageBehaviorStyles = "background-size: cover;";
                                // } else if (slide.fields.responsiveImageBehavior === "center") {
                                //     responsiveImageBehaviorStyles = "background-size: contain;";
                                // } else if (slide.fields.responsiveImageBehavior === "prioritizeHeight") {
                                //     responsiveImageBehaviorStyles = "background-size: auto 100%;";
                                // } else if (slide.fields.responsiveImageBehavior === "prioritizeWidth") {
                                //     responsiveImageBehaviorStyles = "background-size: 100% auto;";
                                // }

                                let imageUrl;

                                if (slide.fields.background === "image") {
                                    imageUrl = slide.fields.image;
                                    if (isTabletScreen) {
                                        imageUrl = slide.fields.mediumImage || imageUrl;
                                    }
                                    if (isMobileScreen) {
                                        imageUrl = slide.fields.smallImage || imageUrl;
                                    }
                                }

                                const backgroundStyles =
                                    slide.fields.background !== "image" &&
                                    `background: linear-gradient(
                                            to right,
                                            transparent 0,
                                            transparent 110px,
                                            ${slide.fields.backgroundColor} 110px,
                                            ${slide.fields.backgroundColor} 100%
                                        ); background-repeat: no-repeat;`;

                                // const focalPointStyles = getFocalPointStyles(slide.fields.focalPoint);

                                // let overlayPositioningStyles;
                                // if (!slide.fields.partialOverlay) {
                                //     overlayPositioningStyles = "align-items: stretch;";
                                // } else if (slide.fields.partialOverlayPositioning === "top") {
                                //     overlayPositioningStyles = "align-items: flex-start;";
                                // } else if (slide.fields.partialOverlayPositioning === "middle") {
                                //     overlayPositioningStyles = "align-items: center;";
                                // } else if (slide.fields.partialOverlayPositioning === "bottom") {
                                //     overlayPositioningStyles = "align-items: flex-end;";
                                // }

                                let fontSizeStyles;
                                if (fields.responsiveFontSizes || fields.customFontSizes) {
                                    fontSizeStyles = responsiveStyleRules(
                                        fields.responsiveFontSizes,
                                        fields.customFontSizes ? fields : undefined,
                                    );
                                }

                                const slideWrapperStyles = {
                                    css: css`
                                        ${styles.slideContentWrapper?.css || ""}
                                        ${textAlignStyles}
                                        ${!isMobileScreen && !isTabletScreen && backgroundStyles}
                                        ${fontSizeStyles}
                                    `,
                                };
                                const slideOverlayWrapperStyles = {
                                    css: css`
                                        ${styles.slideOverlayWrapper?.css || ""}
                                        padding: ${slide.fields.contentPadding}px;
                                        ${xScreen("gt", "md")} {
                                            position: absolute;
                                            top: 50%;
                                            transform: translateY(-50%);
                                        }

                                    `,
                                };
                                const tabletBackgroundStyles = {
                                    css: css`
                                        ${styles.backgroundWrapper?.css || ""}
                                        ${slide.fields.background !== "image"
                                            ? `background-color: ${slide.fields.backgroundColor};`
                                            : ""}
                                    `,
                                };
                                const styledProp = {
                                    textAlignment: fields.textAlignment,
                                };
                                const shouldRenderButtonsWrapper =
                                    slide.fields.buttonLabel ||
                                    slide.fields.buttonLink.value ||
                                    slide.fields.secondButtonLabel ||
                                    slide.fields.secondButtonLink?.value;
                                const shouldRenderSlideContent =
                                    shouldRenderButtonsWrapper || slide.fields.heading || slide.fields.subheading;

                                const imageLinkUrl = imageLink.url;
                                return (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <StyledWrapper className="slide-wrapper" key={index} {...slideWrapperStyles} {...{ slidesHeight }}>
                                        {slide.fields.linkOrParagraph === "link" && (
                                            <Clickable
                                                {...styles.clickableBackground}
                                                {...{ imageLinkUrl }}
                                                href={imageLink?.url}
                                                id={slide.fields.imageLinkElementId || `homepg_banner${index + 1}`}
                                            >
                                                    {slide.fields.background === "image" &&
                                                   ( slide.fields.removeImageIndentation || isTabletScreen) && (
                                                        <StyledWrapper {...styles.imageWrapper} {...{ slidesHeight }}>
                                                            <LazyImage
                                                                {...styles.imageWithoutIdentation}
                                                                src={imageUrl}
                                                                altText={slide.fields.imageAltText}
                                                            />
                                                        </StyledWrapper>)}
                                                {!isTabletScreen &&
                                                    slide.fields.background === "image" &&
                                                    !slide.fields.removeImageIndentation && (
                                                        <>
                                                            <StyledWrapper {...styles.identationBox}></StyledWrapper>
                                                            <StyledWrapper {...styles.imageWrapper} {...{ slidesHeight }}>
                                                                <LazyImage
                                                                    {...styles.imageWithIdentation}
                                                                    src={imageUrl}
                                                                    altText={slide.fields.imageAltText}
                                                                />
                                                            </StyledWrapper>
                                                        </>
                                                    )}
                                            </Clickable>
                                        )}
                                        {slide.fields.linkOrParagraph === "paragraph" && (
                                            <>
                                                {!isTabletScreen &&
                                                    slide.fields.background === "image" &&
                                                    slide.fields.removeImageIndentation && (
                                                        <StyledWrapper {...styles.imageWrapper} {...{ slidesHeight }}>
                                                            <LazyImage
                                                                {...styles.imageWithoutIdentation}
                                                                src={imageUrl}
                                                                altText={slide.fields.imageAltText}
                                                            />
                                                        </StyledWrapper>
                                                    )}
                                                {!isTabletScreen &&
                                                    slide.fields.background === "image" &&
                                                    !slide.fields.removeImageIndentation && (
                                                        <>
                                                            <StyledWrapper {...styles.identationBox}></StyledWrapper>
                                                            <StyledWrapper {...styles.imageWrapper} {...{ slidesHeight }}>
                                                                <LazyImage
                                                                    {...styles.imageWithIdentation}
                                                                    src={imageUrl}
                                                                    altText={slide.fields.imageAltText}
                                                                />
                                                            </StyledWrapper>
                                                        </>
                                                    )}

                                                <StyledWrapper {...slideOverlayWrapperStyles}>
                                                    {(isMobileScreen || isTabletScreen) && (
                                                        <StyledWrapper {...tabletBackgroundStyles}>
                                                            {slide.fields.background === "image" && (
                                                                <LazyImage
                                                                    {...styles.image}
                                                                    src={imageUrl}
                                                                    altText={slide.fields.imageAltText}
                                                                />
                                                            )}
                                                        </StyledWrapper>
                                                    )}
                                                    {shouldRenderSlideContent && (
                                                        <StyledWrapper
                                                            disabled={true}
                                                            {...styles.slideCenteringWrapperStyles}
                                                        >
                                                            {slide.fields.heading && (
                                                                <Typography
                                                                    className="slide-heading"
                                                                    {...styles.headingText}
                                                                >
                                                                    {parse(slide.fields.heading, parserOptions)}
                                                                </Typography>
                                                            )}
                                                            {slide.fields.subheading && (
                                                                <Typography
                                                                    className="slide-subheading"
                                                                    {...styles.subheadingText}
                                                                >
                                                                    {parse(slide.fields.subheading, parserOptions)}
                                                                </Typography>
                                                            )}
                                                            {shouldRenderButtonsWrapper && (
                                                                <StyledWrapper
                                                                    {...styles.buttonsWrapper}
                                                                    {...styledProp}
                                                                >
                                                                    {(slide.fields.buttonLabel ||
                                                                        slide.fields.buttonLink.value) &&
                                                                        (slide.fields.buttonVariant !== "special" ? (
                                                                            <Button
                                                                                className="slide-button"
                                                                                {...styles.slideButton}
                                                                                variant={slide.fields.buttonVariant}
                                                                                onClick={() =>
                                                                                    onClick(
                                                                                        buttonLink?.url,
                                                                                        slide.fields.openInNewTab,
                                                                                    )
                                                                                }
                                                                                id={slide.fields.buttonElementId}
                                                                                aria-label={
                                                                                    slide.fields.buttonLabel ||
                                                                                    buttonLink?.title ||
                                                                                    buttonLink?.url
                                                                                }
                                                                            >
                                                                                {slide.fields.buttonLabel ||
                                                                                    buttonLink?.title ||
                                                                                    buttonLink?.url}
                                                                            </Button>
                                                                        ) : (
                                                                            <IconButton
                                                                                onClick={() =>
                                                                                    onClick(
                                                                                        buttonLink?.url,
                                                                                        slide.fields.openInNewTab,
                                                                                    )
                                                                                }
                                                                                id={slide.fields.buttonElementId}
                                                                                label={
                                                                                    slide.fields.buttonLabel ||
                                                                                    buttonLink?.title ||
                                                                                    buttonLink?.url
                                                                                }
                                                                                iconProps={{
                                                                                    src: CaretRight,
                                                                                    size: 21,
                                                                                }}
                                                                                extendedStyles={...styles.specialButton}
                                                                            />
                                                                        ))}
                                                                    {(slide.fields.secondButtonLabel ||
                                                                        slide.fields.secondButtonLink?.value) &&
                                                                        (slide.fields.secondButtonVariant !==
                                                                        "special" ? (
                                                                            <Button
                                                                                className="slide-button"
                                                                                {...styles.slideButton}
                                                                                variant={
                                                                                    slide.fields.secondButtonVariant
                                                                                }
                                                                                onClick={() =>
                                                                                    onClick(
                                                                                        secondButtonLink?.url,
                                                                                        slide.fields.openInNewTab2,
                                                                                    )
                                                                                }
                                                                                id={slide.fields.secondButtonElementId}
                                                                                aria-label={
                                                                                    slide.fields.secondButtonLabel ||
                                                                                    secondButtonLink?.title ||
                                                                                    secondButtonLink?.url
                                                                                }
                                                                            >
                                                                                {slide.fields.secondButtonLabel ||
                                                                                    secondButtonLink?.title ||
                                                                                    secondButtonLink?.url}
                                                                            </Button>
                                                                        ) : (
                                                                            <IconButton
                                                                                onClick={() =>
                                                                                    onClick(
                                                                                        secondButtonLink?.url,
                                                                                        slide.fields.openInNewTab2,
                                                                                    )
                                                                                }
                                                                                id={slide.fields.secondButtonElementId}
                                                                                label={
                                                                                    slide.fields?.secondButtonLabel ||
                                                                                    secondButtonLink?.title ||
                                                                                    secondButtonLink?.url
                                                                                }
                                                                                iconProps={{
                                                                                    src: CaretRight,
                                                                                    size: 21,
                                                                                }}
                                                                                extendedStyles={...styles.specialButton}
                                                                            />
                                                                        ))}
                                                                </StyledWrapper>
                                                            )}
                                                        </StyledWrapper>
                                                    )}
                                                </StyledWrapper>
                                            </>
                                        )}
                                    </StyledWrapper>
                                );
                            })}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper className="slide-indicator" {...styles.dotsContainerWrapper}>
                {fields.showArrows && fields.slides.length > 1 && (
                    <SlideShowArrow
                        currentIndex={selectedIndex}
                        embla={embla}
                        dir="prev"
                        totalSlides={fields.slides.length}
                    />
                )}
                {fields.slideIndicator &&
                    fields.slides.map((_, i) => {
                        const dotColorStyles =
                            i === selectedIndex
                                ? css`
                                      background: ${color("common.ui6")};
                                      border: 0;
                                  `
                                : "";
                        const dotButtonStyles = {
                            css: css`
                                ${styles.dotButton?.css || ""}
                                ${dotColorStyles}
                            `,
                        };
                        return (
                            <Button
                                // eslint-disable-next-line react/no-array-index-key
                                key={i}
                                {...dotButtonStyles}
                                onClick={() => embla && embla.scrollTo(i)}
                                aria-label={`carousel-indecator-slide-${i + 1}`}
                            />
                        );
                    })}
                {fields.showArrows && fields.slides.length > 1 && (
                    <SlideShowArrow
                        currentIndex={selectedIndex}
                        embla={embla}
                        dir="next"
                        totalSlides={fields.slides.length}
                    />
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: Slideshow,
    definition: {
        group: "Basic",
        icon: "presentation-screen",
        fieldDefinitions: [
            {
                name: fields.height,
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "1/4 Viewport", value: "1/4 viewport" },
                    { displayName: "1/2 Viewport", value: "1/2 viewport" },
                    { displayName: "3/4 Viewport", value: "3/4 viewport" },
                    { displayName: "Full Viewport", value: "fullViewport" },
                ],
                defaultValue: "1/4 viewport",
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                name: fields.textAlignment,
                editorTemplate: "DropDownField",
                defaultValue: "center",
                fieldType: "General",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                name: fields.showArrows,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.slideIndicator,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.autoplay,
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "General",
                tab: settingsTab,
                min: 1,
            },
            {
                name: fields.responsiveFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: true,
            },
            {
                name: fields.customFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                tab: settingsTab,
                defaultValue: false,
            },
            {
                name: fields.normalFontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h1FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: 40,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h2FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: 32,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h3FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h4FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h5FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h6FontSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.slides,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { slideTitle } }) => slideTitle || "Slide",
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "slideTitle",
                        displayName: "Slide Title",
                        editorTemplate: "TextField",
                        defaultValue: "Slide",
                    },
                    {
                        name: "background",
                        displayName: "Background",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Image", value: "image" },
                            { displayName: "Color", value: "color" },
                        ],
                        defaultValue: "image",
                        hideEmptyOption: true,
                    },
                    {
                        name: "image",
                        displayName: "Large Image Path",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "mediumImage",
                        displayName: "Medium Image Path",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "smallImage",
                        displayName: "Small Image Path",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "imageAltText",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    // {
                    //     name: "imageOverlay",
                    //     displayName: "Image Color Overlay",
                    //     editorTemplate: "ColorPickerField",
                    //     defaultValue: "",
                    //     isVisible: widget => widget.fields.background === "image",
                    // },
                    // {
                    //     name: "partialOverlay",
                    //     editorTemplate: "CheckboxField",
                    //     defaultValue: false,
                    // },
                    // {
                    //     name: "partialOverlayPositioning",
                    //     editorTemplate: "DropDownField",
                    //     options: [
                    //         { displayName: "Top", value: "top" },
                    //         { displayName: "Middle", value: "middle" },
                    //         { displayName: "Bottom", value: "bottom" },
                    //     ],
                    //     hideEmptyOption: true,
                    //     defaultValue: "bottom",
                    //     isVisible: widget => widget.fields.partialOverlay,
                    // },
                    // {
                    //     name: "responsiveImageBehavior",
                    //     editorTemplate: "DropDownField",
                    //     options: [
                    //         { displayName: "Cover", value: "cover" },
                    //         { displayName: "Center", value: "center" },
                    //         { displayName: "Prioritize height", value: "prioritizeHeight" },
                    //         { displayName: "Prioritize width", value: "prioritizeWidth" },
                    //     ],
                    //     hideEmptyOption: true,
                    //     defaultValue: "cover",
                    // },
                    {
                        name: "backgroundColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Color",
                        defaultValue: "black",
                        isVisible: widget => widget.fields.background === "color",
                    },
                    // {
                    //     name: "focalPoint",
                    //     displayName: "Focal Point",
                    //     editorTemplate: "DropDownField",
                    //     options: [
                    //         { displayName: "Top Left", value: "topLeft" },
                    //         { displayName: "Top Center", value: "topCenter" },
                    //         { displayName: "Top Right", value: "topRight" },
                    //         { displayName: "Center Left", value: "centerLeft" },
                    //         { displayName: "Center", value: "center" },
                    //         { displayName: "Center Right", value: "centerRight" },
                    //         { displayName: "Bottom Left", value: "bottomLeft" },
                    //         { displayName: "Bottom Center", value: "bottomCenter" },
                    //         { displayName: "Bottom Right", value: "bottomRight" },
                    //     ],
                    //     defaultValue: "center",
                    //     hideEmptyOption: true,
                    // },
                    // XNG-Change
                    {
                        name: "linkOrParagraph",
                        displayName: "Use background link or box with text and buttons",
                        editorTemplate: "RadioButtonsField",
                        defaultValue: "link",
                        options: [
                            {
                                displayName: "Background link",
                                value: "link",
                            },
                            {
                                displayName: "Paragraph and buttons",
                                value: "paragraph",
                            },
                        ],
                    },
                    {
                        name: "imageLink",
                        displayName: "Image Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isVisible: widget => widget.fields.linkOrParagraph === "link",
                    },
                    {
                        name: "imageLinkElementId",
                        displayName: "Image Link Element ID",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.linkOrParagraph === "link",
                    },
                    {
                        name: "heading",
                        editorTemplate: "RichTextField",
                        displayName: "Heading",
                        defaultValue: "",
                        extendedConfig: { height: 100 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                        isVisible: widget => widget.fields.linkOrParagraph === "paragraph",
                    },
                    {
                        name: "subheading",
                        editorTemplate: "RichTextField",
                        displayName: "Subheading",
                        defaultValue: "",
                        extendedConfig: { height: 170 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                        isVisible: widget => widget.fields.linkOrParagraph === "paragraph",
                    },
                    {
                        name: "buttonLabel",
                        editorTemplate: "TextField",
                        displayName: "Button Label",
                        defaultValue: "",
                        isVisible: widget => widget.fields.linkOrParagraph === "paragraph",
                    },
                    {
                        name: "buttonLink",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isVisible: widget => widget.fields.linkOrParagraph === "paragraph",
                    },
                    {
                        name: "buttonVariant",
                        displayName: "Button Variant",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Primary", value: "primary" },
                            { displayName: "Secondary", value: "secondary" },
                            { displayName: "Tertiary", value: "tertiary" },
                            { displayName: "Special (Button with icon)", value: "special" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "primary",
                        isVisible: widget => widget.fields.linkOrParagraph === "paragraph",
                    },
                    {
                        name: "buttonElementId",
                        displayName: "Button Element ID",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.linkOrParagraph === "paragraph",
                    },
                    {
                        name: "openInNewTab",
                        displayName: "Open in New Tab",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: widget => widget.fields.linkOrParagraph === "paragraph",
                    },
                    {
                        name: "secondButtonLabel",
                        editorTemplate: "TextField",
                        displayName: "Second Button Label",
                        defaultValue: "",
                        isVisible: widget => widget.fields.linkOrParagraph === "paragraph",
                    },
                    {
                        name: "secondButtonLink",
                        displayName: "Second Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isVisible: widget => widget.fields.linkOrParagraph === "paragraph",
                    },
                    {
                        name: "secondButtonVariant",
                        displayName: "Second Button Variant",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Primary", value: "primary" },
                            { displayName: "Secondary", value: "secondary" },
                            { displayName: "Tertiary", value: "tertiary" },
                            { displayName: "Special (Button with icon)", value: "special" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "primary",
                        isVisible: widget => widget.fields.linkOrParagraph === "paragraph",
                    },
                    {
                        name: "secondButtonElementId",
                        displayName: "Second Button Element ID",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.linkOrParagraph === "paragraph",
                    },
                    {
                        name: "openInNewTab2",
                        displayName: "Open in New Tab",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: widget => widget.fields.linkOrParagraph === "paragraph",
                    },
                    {
                        name: "contentPadding",
                        editorTemplate: "IntegerField",
                        defaultValue: 0,
                        isVisible: widget => widget.fields.linkOrParagraph === "paragraph",
                    },
                    // {
                    //     name: "centerTextVertically",
                    //     editorTemplate: "CheckboxField",
                    //     defaultValue: false,
                    // },
                    {
                        name: "removeImageIndentation",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: widget => widget.fields.background === "image",
                    },
                ],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
